import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import api from '../../utils/axiosConfig'

const EditProfile = () => {
  const navigate = useNavigate()
  const [states, setStates] = useState([])
  const [loading, setLoading] = useState(false)
  const [fetchingProfile, setFetchingProfile] = useState(true)
  const [formData, setFormData] = useState({
    association_name: '',
    state_id: '',
    president: '',
    secretary: '',
    official_contact: '',
    official_email: '',
    address: ''
  })
  const [errors, setErrors] = useState({})
  const [originalData, setOriginalData] = useState({})
  const [reason, setReason] = useState('')

  useEffect(() => {
    fetchStates()
    fetchProfile()
  }, [])

  const fetchStates = async () => {
    try {
      const response = await api.get('/user/states')
      if (response.data.success) {
        setStates(response.data.data)
      }
    } catch (error) {
      console.error('Error fetching states:', error)
    }
  }

  const fetchProfile = async () => {
    try {
      setFetchingProfile(true)
      const response = await api.get('/user/profile')
      if (response.data.success) {
        const profile = response.data.data
        const profileData = {
          association_name: profile.association_name || '',
          state_id: profile.state_id || '',
          president: profile.president || '',
          secretary: profile.secretary || '',
          official_contact: profile.official_contact || '',
          official_email: profile.official_email || '',
          address: profile.address || ''
        }
        setFormData(profileData)
        setOriginalData({ ...profileData, user_id: profile.id })
      }
    } catch (error) {
      console.error('Error fetching profile:', error)
      alert('Failed to load profile data')
    } finally {
      setFetchingProfile(false)
    }
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target

    // For phone number field, only allow digits and limit to 10
    if (name === 'official_contact') {
      const digitsOnly = value.replace(/\D/g, '').slice(0, 10)
      setFormData(prev => ({ ...prev, [name]: digitsOnly }))
      return
    }

    setFormData(prev => ({ ...prev, [name]: value }))

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }))
    }
  }

  const validateForm = () => {
    const newErrors = {}

    if (!formData.association_name?.trim()) {
      newErrors.association_name = 'Association name is required'
    }
    if (!formData.state_id) {
      newErrors.state_id = 'State is required'
    }
    if (!formData.president?.trim()) {
      newErrors.president = 'President name is required'
    }
    if (!formData.secretary?.trim()) {
      newErrors.secretary = 'Secretary name is required'
    }
    if (!formData.official_contact?.trim()) {
      newErrors.official_contact = 'Contact number is required'
    } else if (!/^\d{10}$/.test(formData.official_contact)) {
      newErrors.official_contact = 'Contact must be 10 digits'
    }
    if (!formData.official_email?.trim()) {
      newErrors.official_email = 'Email is required'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.official_email)) {
      newErrors.official_email = 'Invalid email format'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validateForm()) return

    // Find changed fields
    const requestedChanges = {}
    Object.keys(formData).forEach(key => {
      if (String(formData[key]) !== String(originalData[key])) {
        requestedChanges[key] = formData[key]
      }
    })

    if (Object.keys(requestedChanges).length === 0) {
      alert('No changes detected. Please modify at least one field.')
      return
    }

    if (!reason.trim() || reason.trim().length < 10) {
      setErrors(prev => ({ ...prev, reason: 'Please provide a reason (minimum 10 characters)' }))
      return
    }

    setLoading(true)
    try {
      const response = await api.post('/user/member-edit-requests', {
        member_id: originalData.user_id,
        member_type: 'user_profile',
        requested_changes: requestedChanges,
        reason: reason.trim()
      })
      if (response.data.success) {
        alert('Edit request submitted successfully! Admin will review your changes.')
        navigate('/user/profile')
      }
    } catch (error) {
      console.error('Error submitting edit request:', error)
      alert(error.response?.data?.message || 'Failed to submit edit request')
    } finally {
      setLoading(false)
    }
  }

  if (fetchingProfile) {
    return (
      <div className="edit-profile-page">
        <div className="loading-container">
          <i className="fas fa-spinner fa-spin"></i>
          <p>Loading profile data...</p>
        </div>
        <style>{`
          .edit-profile-page {
            padding: 0;
            width: 100%;
            min-height: 100vh;
            background: #f3f4f6;
          }
          .loading-container {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            min-height: 50vh;
            color: #6b7280;
          }
          .loading-container i {
            font-size: 2rem;
            margin-bottom: 1rem;
          }
        `}</style>
      </div>
    )
  }

  return (
    <div className="edit-profile-page">
      {/* Page Header */}
      <div className="page-header-banner">
        <div className="header-content">
          <div className="header-icon">
            <i className="fas fa-user-edit"></i>
          </div>
          <div className="header-text">
            <h1>Edit Profile Request</h1>
            <p>Submit changes for admin approval</p>
          </div>
        </div>
        <button className="btn-back" style={{ marginLeft: 'auto' }} onClick={() => navigate('/user/profile')}>
          <i className="fas fa-arrow-left"></i> Back
        </button>
      </div>

      <div className="form-container">
        <form onSubmit={handleSubmit}>
          {/* Association Information Section */}
          <div className="form-section">
            <h3><i className="fas fa-building"></i> Association Information</h3>
            <div className="form-grid">
              <div className="form-group full-width">
                <label>Association Name <span className="required">*</span></label>
                <input
                  type="text"
                  name="association_name"
                  value={formData.association_name}
                  onChange={handleInputChange}
                  placeholder="Enter Association Name"
                  className={errors.association_name ? 'error' : ''}
                />
                {errors.association_name && <span className="error-text">{errors.association_name}</span>}
              </div>
              <div className="form-group">
                <label>State <span className="required">*</span></label>
                <select
                  name="state_id"
                  value={formData.state_id}
                  onChange={handleInputChange}
                  className={errors.state_id ? 'error' : ''}
                >
                  <option value="">Select State</option>
                  {states.map(state => (
                    <option key={state.id} value={state.id}>{state.name}</option>
                  ))}
                </select>
                {errors.state_id && <span className="error-text">{errors.state_id}</span>}
              </div>
              <div className="form-group">
                <label>President <span className="required">*</span></label>
                <input
                  type="text"
                  name="president"
                  value={formData.president}
                  onChange={handleInputChange}
                  placeholder="Enter President Name"
                  className={errors.president ? 'error' : ''}
                />
                {errors.president && <span className="error-text">{errors.president}</span>}
              </div>
              <div className="form-group">
                <label>Secretary <span className="required">*</span></label>
                <input
                  type="text"
                  name="secretary"
                  value={formData.secretary}
                  onChange={handleInputChange}
                  placeholder="Enter Secretary Name"
                  className={errors.secretary ? 'error' : ''}
                />
                {errors.secretary && <span className="error-text">{errors.secretary}</span>}
              </div>
            </div>
          </div>

          {/* Contact Information Section */}
          <div className="form-section">
            <h3><i className="fas fa-phone-alt"></i> Contact Information</h3>
            <div className="form-grid">
              <div className="form-group">
                <label>Official Contact No <span className="required">*</span></label>
                <input
                  type="tel"
                  name="official_contact"
                  value={formData.official_contact}
                  onChange={handleInputChange}
                  placeholder="Enter 10-digit Mobile Number"
                  maxLength="10"
                  inputMode="numeric"
                  className={errors.official_contact ? 'error' : ''}
                />
                {errors.official_contact && <span className="error-text">{errors.official_contact}</span>}
              </div>
              <div className="form-group">
                <label>Official Email ID <span className="required">*</span></label>
                <input
                  type="email"
                  name="official_email"
                  value={formData.official_email}
                  onChange={handleInputChange}
                  placeholder="Enter Email ID"
                  className={errors.official_email ? 'error' : ''}
                />
                {errors.official_email && <span className="error-text">{errors.official_email}</span>}
              </div>
              <div className="form-group full-width">
                <label>Address</label>
                <textarea
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  placeholder="Enter Full Address"
                  rows="3"
                />
              </div>
            </div>
          </div>

          {/* Reason for Edit */}
          <div className="form-section" style={{ border: '2px solid #fbbf24', background: '#fffbeb', borderRadius: '12px', padding: '1.5rem', marginBottom: '1.5rem' }}>
            <h2 style={{ fontSize: '1.1rem', color: '#92400e', margin: '0 0 0.5rem 0', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <i className="fas fa-comment-alt"></i> Reason for Edit Request
            </h2>
            <p style={{ fontSize: '0.85rem', color: '#a16207', margin: '0 0 1rem 0' }}>
              Your changes will be reviewed by admin before being applied.
            </p>
            <textarea
              value={reason}
              onChange={(e) => { setReason(e.target.value); if (errors.reason) setErrors(prev => ({ ...prev, reason: '' })) }}
              placeholder="Please explain why you need these changes (minimum 10 characters)..."
              rows="3"
              style={{
                width: '100%',
                padding: '0.75rem',
                border: errors.reason ? '2px solid #ef4444' : '2px solid #d1d5db',
                borderRadius: '8px',
                fontSize: '0.9rem',
                fontFamily: 'inherit',
                resize: 'vertical',
                boxSizing: 'border-box'
              }}
            />
            {errors.reason && <span className="error-text">{errors.reason}</span>}
          </div>

          {/* Form Actions */}
          <div className="form-actions">
            <button type="button" className="btn-cancel" onClick={() => navigate('/user/profile')}>
              <i className="fas fa-times"></i> Cancel
            </button>
            <button type="submit" className="btn-submit" disabled={loading}>
              {loading ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i> Submitting...
                </>
              ) : (
                <>
                  <i className="fas fa-paper-plane"></i> Submit Edit Request
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      <style>{`
        .edit-profile-page {
          padding: 0;
          width: 100%;
          min-height: 100vh;
          background: #f3f4f6;
        }

        .page-header-banner {
          background: var(--primary-gradient, linear-gradient(to right, #022a5e, #053e88, #022a5e));
          padding: 1.5rem 2rem;
          display: flex;
          align-items: center;
          gap: 1.5rem;
          border-radius: 12px;
          margin: 1.5rem;
          margin-bottom: 0;
        }

        .btn-back {
          height: 40px;
          padding: 0 18px;
          border-radius: 8px;
          border: none;
          background: #c00f15;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 8px;
          justify-content: center;
          color: white;
          font-weight: 600;
          font-size: 0.9rem;
          white-space: nowrap;
          transition: all 0.2s;
          flex-shrink: 0;
        }

        .btn-back:hover {
          background: #a00d12;
        }

        .header-content {
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .header-icon {
          width: 50px;
          height: 50px;
          background: rgba(255,255,255,0.15);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .header-icon i {
          font-size: 1.5rem;
          color: white;
        }

        .header-text h1 {
          font-size: 1.5rem;
          font-weight: 600;
          color: white;
          margin: 0;
        }

        .header-text p {
          color: rgba(255,255,255,0.7);
          margin: 0;
          font-size: 0.875rem;
        }

        .form-container {
          background: white;
          margin: 1.5rem;
          border-radius: 12px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
          overflow: hidden;
        }

        .form-section {
          padding: 1.5rem 2rem;
          border-bottom: 1px solid #e5e7eb;
        }

        .form-section:last-of-type {
          border-bottom: none;
        }

        .form-section h3 {
          font-size: 1rem;
          font-weight: 600;
          color: #374151;
          margin: 0 0 1.25rem 0;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding-bottom: 0.75rem;
          border-bottom: 1px solid #e5e7eb;
        }

        .form-section h3 i {
          color: var(--primary-color, #022a5e);
          font-size: 0.9rem;
        }

        .form-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 1.25rem;
        }

        .form-group {
          display: flex;
          flex-direction: column;
        }

        .form-group.full-width {
          grid-column: span 2;
        }

        .form-group label {
          font-size: 0.875rem;
          font-weight: 500;
          color: #374151;
          margin-bottom: 0.5rem;
        }

        .required {
          color: #ef4444;
        }

        .form-group input,
        .form-group select,
        .form-group textarea {
          padding: 0.75rem;
          border: 1px solid #d1d5db;
          border-radius: 8px;
          font-size: 0.9rem;
          transition: all 0.2s;
          font-family: inherit;
        }

        .form-group input.error,
        .form-group select.error,
        .form-group textarea.error {
          border-color: #ef4444;
        }

        .form-group textarea {
          resize: vertical;
          min-height: 80px;
        }

        .form-group input:focus,
        .form-group select:focus,
        .form-group textarea:focus {
          outline: none;
          border-color: var(--primary-color, #022a5e);
          box-shadow: 0 0 0 3px rgba(2, 42, 94, 0.1);
        }

        .form-group input::placeholder {
          color: #9ca3af;
        }

        .error-text {
          color: #ef4444;
          font-size: 0.75rem;
          margin-top: 0.25rem;
        }

        .form-actions {
          padding: 1.5rem 2rem;
          background: #f9fafb;
          display: flex;
          justify-content: flex-end;
          gap: 1rem;
          border-top: 1px solid #e5e7eb;
        }

        .btn-cancel {
          padding: 0.75rem 1.5rem;
          background: white;
          color: #6b7280;
          border: 1px solid #d1d5db;
          border-radius: 8px;
          cursor: pointer;
          font-weight: 500;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          transition: all 0.2s;
        }

        .btn-cancel:hover {
          background: #f3f4f6;
          color: #374151;
        }

        .btn-submit {
          padding: 0.75rem 1.5rem;
          background: var(--primary-color, #022a5e);
          color: white;
          border: none;
          border-radius: 8px;
          cursor: pointer;
          font-weight: 500;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          transition: all 0.2s;
        }

        .btn-submit:hover:not(:disabled) {
          background: var(--primary-hover, #053e88);
        }

        .btn-submit:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        @media (max-width: 768px) {
          .form-grid {
            grid-template-columns: 1fr;
          }

          .form-group.full-width {
            grid-column: span 1;
          }

          .form-actions {
            flex-direction: column;
          }

          .btn-cancel, .btn-submit {
            width: 100%;
            justify-content: center;
          }
        }
      `}</style>
    </div>
  )
}

export default EditProfile
